import { useDispatch, useSelector } from "react-redux";
import useAuth from "./useAuth";
import { useContext, useState } from "react";
import { AppContext } from "context/appContext";
import api from "configs/api";
import { setEmployees } from "slices/companySlice";
import { RootState } from "configs/store";
import Swal from "sweetalert2";

interface EmployeeField {
  placeholder: string;
  name: string;
  type: string;
  size: string;
  optionsLabel?: string;
  options?: any[];
}

interface Employee {
  _id: string;
  name: string;
  email: string;
  position: string;
  branchID?: {
    _id: string;
    name: string;
  };
  status: boolean;
  createdAt: string;
  imageURL?: string;
  [key: string]: any;
}

interface EmployeeFilter {
  companyID?: string;
  page?: number;
  limit?: number;
  [key: string]: any;
}

const DeleteAlert = async (): Promise<boolean> => {
  const result = await Swal.fire({
    title: "Are you sure?",
    text: "You won't be able to revert this!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "Yes, delete it!",
  });
  return result.isConfirmed;
};

export default function useEmployee() {
  const { employees } = useSelector((state: RootState) => state.company);
  const dispatch = useDispatch();

  const [addEmployeeLoading, setAddEmployeeLoading] = useState<boolean>(false);
  const [showAddEmployee, setShowAddEmployee] = useState<boolean>(false);
  const [isEmployeeUpdate, setIsEmployeeUpdate] = useState<boolean>(false);
  const [isEmployeeDeleting, setIsEmployeeDeleting] = useState<string | null>(null);

  const { showAlert } = useContext(AppContext);
  const { isManager, isCompany, user } = useAuth();

  const companyEmployeeColumns = [
    { name: "employee", align: "left" as const },
    { name: "position", align: "left" as const },
    { name: "site", align: "left" as const },
    { name: "status", align: "left" as const },
    { name: "action", align: "center" as const },
  ];

  const employeeFields: EmployeeField[] = [
    {
      placeholder: "Member Name",
      name: "name",
      type: "text",
      size: "medium",
    },
    {
      placeholder: "Member Email",
      name: "email",
      type: "email",
      size: "medium",
    },
    ...(isManager
      ? []
      : [
          {
            placeholder: "Select Site...",
            name: "branchID",
            type: "dropdown",
            size: "medium",
            options: [],
          } as EmployeeField,
        ]),
    {
      placeholder: "Select Role...",
      name: "position",
      type: "dropdown",
      size: "medium",
      optionsLabel: "name",
      options: [
        { _id: "Supervisor", name: "Supervisor" },
        { _id: "Guest", name: "Guest" },
        ...(isManager ? [] : [{ _id: "Manager", name: "Manager" }]),
      ].filter((item) => item),
    },
  ].filter((item) => item) as EmployeeField[];

  const getCompanyEmployees = async (filter: EmployeeFilter): Promise<void> => {
    try {
      const response = await api.get(`auth/employee`, {
        params: filter,
      });
      if (response.data.status) {
        dispatch(setEmployees(response.data.result || []));
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const addEmployee = async (data: any): Promise<void> => {
    setAddEmployeeLoading(true);
    try {
      const response = await api.post(`auth/employee`, {
        ...data,
        companyID: isCompany ? user?._id : user?.companyID,
      });
      if (response.data.status) {
        await getCompanyEmployees({ companyID: isCompany ? user?._id : user?.companyID });
        setShowAddEmployee(false);
        showAlert("Employee added successfully!", "success", "Add Employee");
      } else {
        showAlert(response.data.message, "error", "Add Employee");
      }
    } catch (error: any) {
      showAlert(error.message || "Failed to add employee", "error", "Add Employee");
    } finally {
      setAddEmployeeLoading(false);
    }
  };

  const updateEmployee = async (data: any): Promise<void> => {
    setAddEmployeeLoading(true);
    try {
      const response = await api.put(`auth/employee`, { ...data });
      if (response.data.status) {
        await getCompanyEmployees({ companyID: isCompany ? user?._id : user?.companyID });
        setShowAddEmployee(false);
        setIsEmployeeUpdate(false);
        showAlert("Employee updated successfully!", "success", "Update Employee");
      } else {
        showAlert(response.data.message, "error", "Update Employee");
      }
    } catch (error: any) {
      showAlert(error.message || "Failed to update employee", "error", "Update Employee");
    } finally {
      setAddEmployeeLoading(false);
    }
  };

  const deleteEmployee = async (employeeID: string): Promise<void> => {
    try {
      if (!(await DeleteAlert())) return;
      setIsEmployeeDeleting(employeeID);
      const response = await api.delete(`auth/employee`, { data: { _id: employeeID } });
      if (response.data.status) {
        await getCompanyEmployees({ companyID: isCompany ? user?._id : user?.companyID });
        showAlert("Employee deleted successfully!", "success", "Delete Employee");
      } else {
        showAlert(response.data.message, "error", "Delete Employee");
      }
    } catch (error: any) {
      showAlert(error.message || "Failed to delete employee", "error", "Delete Employee");
    } finally {
      setIsEmployeeDeleting(null);
    }
  };

  return {
    employeeFields,
    companyEmployeeColumns,
    deleteEmployee,
    updateEmployee,
    addEmployee,
    getCompanyEmployees,
    employees: employees as Employee[],
    addEmployeeLoading,
    setAddEmployeeLoading,
    showAddEmployee,
    setShowAddEmployee,
    isEmployeeUpdate,
    setIsEmployeeUpdate,
    isEmployeeDeleting,
    setIsEmployeeDeleting,
  };
}

